import React, { useState, useEffect, useRef } from "react";
import { socket } from "../socket";
import { useGameTimer } from "../gameTimer";
import { PlayerAvatar, PlayerName, bannerStyle } from "./PlayerIdentity";
import { t, tx } from "../i18n";
import { useSounds, useCountdownTick } from "../sound";
import { alertDialog } from "../dialog";

// Quiz screen for Imposter mode. Unlike Culture mode, the pace is set by the
// server: we display the question it sends and send the input back, never
// deciding on our own to move to the next one.
function ImposterQuiz({ gameId, player }) {
  const [question, setQuestion]   = useState(null);
  const [startedAt, setStartedAt] = useState(null);
  const [duration, setDuration]   = useState(20000);
  const [answer, setAnswer]       = useState("");
  const [players, setPlayers]     = useState([]);
  const [variant, setVariant]     = useState(null);
  const [waiting, setWaiting]     = useState(true);
  const [now, setNow]             = useState(Date.now());
  // Rounds
  const [round, setRound]         = useState(null);   // { round, roundTotal, roundQuestion, roundSize }
  const [myVote, setMyVote]       = useState(null);
  const [voteError, setVoteError] = useState("");
  const [reveal, setReveal]       = useState(null);   // end-of-round reveal
  // The Mole
  const [taupeActive, setTaupeActive] = useState(false);
  const [reignReveal, setReignReveal] = useState(null);
  // Volunteer
  const [bluffWindow, setBluffWindow] = useState(null);  // { index, startedAt, duration }
  const [iBluff, setIBluff]           = useState(false);
  const [bluffTokens, setBluffTokens] = useState(3);
  const [accuseTokens, setAccuseTokens] = useState(3);
  const [bluffReveal, setBluffReveal] = useState(null);
  const inputRef = useRef(null);

  const myId = player?.playerId;
  const { playPop } = useSounds();

  useEffect(() => {
    const applyRound = (d) => {
      if (d.taupeActive !== undefined) setTaupeActive(!!d.taupeActive);
      if (d.iBluff !== undefined) setIBluff(!!d.iBluff);
      if (d.bluffTokens !== undefined) setBluffTokens(d.bluffTokens);
      if (d.accuseTokens !== undefined) setAccuseTokens(d.accuseTokens);
      if (d.myVote !== undefined) setMyVote(d.myVote || null);
      if (d.round === undefined) return;
      setRound({ round: d.round, roundTotal: d.roundTotal, roundQuestion: d.roundQuestion, roundSize: d.roundSize });
    };

    socket.on("impState", (s) => {
      setVariant(s.variant); setPlayers(s.players); setDuration(s.duration);
      applyRound(s);
      if (s.question && s.startedAt) {
        setQuestion(s.question); setStartedAt(s.startedAt);
        setAnswer(s.myAnswer || ""); setWaiting(false);
      } else {
        setWaiting(true);
      }
    });
    socket.on("impQuestion", (q) => {
      setQuestion(q); setStartedAt(q.startedAt); setDuration(q.duration);
      setAnswer(""); setWaiting(false); setReveal(null); setReignReveal(null);
      setBluffReveal(null); setBluffWindow(null); setVoteError("");
      applyRound(q);
      inputRef.current?.focus();
    });
    socket.on("impQuestionEnd", () => { setWaiting(true); setStartedAt(null); });
    socket.on("impVoteResult", (r) => {
      if (r.ok) { setMyVote(r.accusedId); setVoteError(""); if (r.accuseTokens !== undefined) setAccuseTokens(r.accuseTokens); }
      else { setVoteError(r.error || t("imposter.voteFailed")); setTimeout(() => setVoteError(""), 2500); }
    });
    socket.on("impRoundEnd", (r) => {
      playPop();
      setReveal(r); setPlayers(r.players); setWaiting(true); setStartedAt(null);
    });
    socket.on("impReignEnd", (r) => {
      setReignReveal(r); setPlayers(r.players);
      setWaiting(true); setStartedAt(null); setTaupeActive(false); setMyVote(null);
    });
    socket.on("impBluffWindow", (w) => {
      setBluffWindow(w); setIBluff(false); setQuestion(null); setStartedAt(null);
      setBluffTokens(w.bluffTokens); setAccuseTokens(w.accuseTokens);
      setBluffReveal(null); setReveal(null); setReignReveal(null);
      setWaiting(false); setMyVote(null);
    });
    socket.on("impBluffResult", (r) => {
      if (r.ok) { setIBluff(true); setBluffTokens(r.bluffTokens); }
      else { setVoteError(r.error || ""); setTimeout(() => setVoteError(""), 2500); }
    });
    socket.on("impBluffEnd", (r) => {
      setBluffReveal(r); setPlayers(r.players);
      setWaiting(true); setStartedAt(null); setMyVote(null);
    });
    socket.on("impError", ({ error }) => alertDialog(error));

    socket.emit("impGetState", { gameId });
    return () => {
      ["impState", "impQuestion", "impQuestionEnd", "impVoteResult", "impRoundEnd", "impReignEnd",
       "impBluffWindow", "impBluffResult", "impBluffEnd", "impError"]
        .forEach(e => socket.off(e));
    };
  }, [gameId, playPop]);

  useEffect(() => {
    if (!startedAt) return;
    const iv = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(iv);
  }, [startedAt]);

  // Input is pushed to the server as it is typed: if time runs out or the player
  // disconnects, their last answer is already recorded.
  const onChange = (value) => {
    setAnswer(value);
    if (question && startedAt) {
      socket.emit("impAnswer", { gameId, index: question.index, answer: value });
    }
  };

  const accuse = (accusedId) => {
    if (myVote || accusedId === myId) return;
    socket.emit("impVote", { gameId, accusedId });
  };

  const remaining = startedAt ? Math.max(0, Math.ceil((duration - (now - startedAt)) / 1000)) : 0;

  useCountdownTick(remaining, { active: !!startedAt });
  useGameTimer(startedAt ? { remaining, duration } : null);

  if (!question && !reveal && !reignReveal && !bluffReveal && !bluffWindow) {
    return <div className="ImposterQuiz"><p className="imp-waiting">{t("imposter.preparing")}</p></div>;
  }

  const isImposter = question?.role === "imposteur";
  const canVote = !myVote && !reveal && !reignReveal && !bluffReveal
    && (["manches", "taupe"].includes(variant)
        || (variant === "volontaire" && !!question && accuseTokens > 0));
  const pseudoOf = (pid) => players.find(p => p.playerId === pid)?.pseudo || "?";

  return (
    <div className={`ImposterQuiz${isImposter ? " is-imposter" : ""}`}>
      <div className="imp-header">
        <div className="imp-header-info">
          {variant === "manches" && round && (
            <span className="imp-round-badge">
              {t("imposter.roundBadge", {
                round: round.round + 1, total: round.roundTotal,
                q: round.roundQuestion, qTotal: round.roundSize,
              })}
            </span>
          )}
          {variant === "volontaire" && (
            <span className="imp-tokens">
              {t("imposter.tokens", {
                bluffs: t("imposter.bluffCount", { count: bluffTokens }),
                accusations: t("imposter.accusationCount", { count: accuseTokens }),
              })}
            </span>
          )}
          {/* Public alert: everyone knows a mole is at work, never who or since when */}
          {variant === "taupe" && taupeActive && (
            <span className="imp-taupe-alert">{t("imposter.moleAlert")}</span>
          )}
          <p className="imp-progress">{(question?.index ?? 0) + 1} / {question?.total ?? "?"}</p>
        </div>
      </div>

      <div className="imp-main">
        {bluffWindow ? (
          <div className="imp-bluff-window">
            <span className="imp-reveal-label">{t("imposter.questionOf", { index: bluffWindow.index + 1, total: bluffWindow.total })}</span>
            <h3 className="imp-bluff-title">
              {iBluff ? t("imposter.bluffTitleOn") : t("imposter.bluffTitleOff")}
            </h3>
            <p className="imp-bluff-hint">
              {iBluff ? t("imposter.bluffHintOn") : t("imposter.bluffHintOff")}
            </p>
            <button className="btn btn-primary imp-bluff-btn"
              disabled={iBluff || bluffTokens <= 0}
              onClick={() => socket.emit("impBluff", { gameId })}>
              {iBluff ? t("imposter.bluffEngaged")
                : bluffTokens > 0 ? t("imposter.bluffButton", { count: bluffTokens })
                : t("imposter.noTokens")}
            </button>
            {voteError && <p className="imp-vote-error">{voteError}</p>}
          </div>
        ) : bluffReveal ? (
          <div className="imp-reveal">
            <span className="imp-reveal-label">{t("imposter.questionN", { index: bluffReveal.index + 1 })}</span>
            {bluffReveal.bluffers.length === 0 ? (
              <h3 className="imp-reveal-title">{t("imposter.nobodyBluffed")}</h3>
            ) : (
              <h3 className="imp-reveal-title">
                🎭 {bluffReveal.bluffers.map((b, i) => (
                  <span key={b.playerId}>
                    {i > 0 && ", "}
                    <b>{b.pseudo}</b> {b.caught ? t("imposter.caught") : t("imposter.gotAway")}
                  </span>
                ))}
              </h3>
            )}
            <div className="imp-reveal-votes">
              {Object.entries(bluffReveal.votes || {}).map(([voter, accused]) => (
                <span key={voter} className={`imp-reveal-vote${bluffReveal.bluffers.some(b => b.playerId === accused) ? " good" : " bad"}`}>
                  {pseudoOf(voter)} → {pseudoOf(accused)}
                </span>
              ))}
            </div>
            <div className="imp-reveal-scores">
              {players.map(p => (
                <div key={p.playerId} className="imp-reveal-score">
                  <PlayerAvatar player={p} size={26} />
                  <PlayerName player={p} />
                  <b>{p.score}</b>
                  {bluffReveal.deltas?.[p.playerId] ? (
                    <em className={bluffReveal.deltas[p.playerId] > 0 ? "up" : "down"}>
                      {bluffReveal.deltas[p.playerId] > 0 ? "+" : ""}{bluffReveal.deltas[p.playerId]}
                    </em>
                  ) : null}
                </div>
              ))}
            </div>
          </div>
        ) : reignReveal ? (
          <div className="imp-reveal">
            <span className="imp-reveal-label">
              {reignReveal.reason === "caught" ? t("imposter.moleCaught")
                : reignReveal.reason === "retired" ? t("imposter.moleRetired")
                : t("imposter.falseAlarm")}
            </span>
            {reignReveal.imposterId ? (
              <h3 className="imp-reveal-title">
                🎭 {tx("imposter.reigned", {
                  name: <><b>{reignReveal.imposterPseudo}</b>{reignReveal.imposterId === myId && t("imposter.reignedYou")}</>,
                  count: <b>{t("imposter.questionCount", { count: reignReveal.questions })}</b>,
                })}
                {reignReveal.caughtByPseudo && tx("imposter.unmaskedBy", { name: <b>{reignReveal.caughtByPseudo}</b> })}
              </h3>
            ) : (
              <h3 className="imp-reveal-title">{tx("imposter.noMole", { bold: <b>{t("imposter.noMoleBold")}</b> })}</h3>
            )}
            <div className="imp-reveal-votes">
              {Object.entries(reignReveal.votes || {}).map(([voter, accused]) => (
                <span key={voter} className={`imp-reveal-vote${accused === reignReveal.imposterId ? " good" : " bad"}`}>
                  {pseudoOf(voter)} → {pseudoOf(accused)}
                </span>
              ))}
            </div>
            <div className="imp-reveal-scores">
              {players.map(p => (
                <div key={p.playerId} className="imp-reveal-score">
                  <PlayerAvatar player={p} size={26} />
                  <PlayerName player={p} />
                  <b>{p.score}</b>
                  {reignReveal.deltas?.[p.playerId] ? (
                    <em className={reignReveal.deltas[p.playerId] > 0 ? "up" : "down"}>
                      {reignReveal.deltas[p.playerId] > 0 ? "+" : ""}{reignReveal.deltas[p.playerId]}
                    </em>
                  ) : null}
                </div>
              ))}
            </div>
          </div>
        ) : reveal ? (
          <div className="imp-reveal">
            <span className="imp-reveal-label">{t("imposter.roundEnd", { round: reveal.round + 1 })}</span>
            {reveal.imposterId ? (
              <h3 className="imp-reveal-title">
                {tx("imposter.imposterWas", { name: <b>{reveal.imposterPseudo}</b> })}
                {reveal.imposterId === myId && t("imposter.imposterWasYou")}
              </h3>
            ) : (
              <h3 className="imp-reveal-title">{tx("imposter.noImposter", { bold: <b>{t("imposter.noImposterBold")}</b> })}</h3>
            )}
            <div className="imp-reveal-votes">
              {Object.keys(reveal.votes || {}).length === 0
                ? <span className="imp-reveal-none">{t("imposter.nobodyAccused")}</span>
                : Object.entries(reveal.votes).map(([voter, accused]) => (
                    <span key={voter} className={`imp-reveal-vote${accused === reveal.imposterId ? " good" : " bad"}`}>
                      {pseudoOf(voter)} → {pseudoOf(accused)}
                    </span>
                  ))}
            </div>
            <div className="imp-reveal-scores">
              {players.map(p => (
                <div key={p.playerId} className="imp-reveal-score">
                  <PlayerAvatar player={p} size={26} />
                  <PlayerName player={p} />
                  <b>{p.score}</b>
                  {reveal.deltas?.[p.playerId] ? (
                    <em className={reveal.deltas[p.playerId] > 0 ? "up" : "down"}>
                      {reveal.deltas[p.playerId] > 0 ? "+" : ""}{reveal.deltas[p.playerId]}
                    </em>
                  ) : null}
                </div>
              ))}
            </div>
          </div>
        ) : (
          <>
            {question.media_url && (
              /\.(jpe?g|png|gif|webp)$/i.test(question.media_url)
                ? <img src={question.media_url} alt="" className="quiz-media" />
                : /\.(mp4|webm)$/i.test(question.media_url)
                ? <video controls src={question.media_url} className="quiz-media" />
                : <audio controls src={question.media_url} />
            )}
            <h3 className="imp-question">{question.question}</h3>
            {isImposter && (
              <span className="imp-role-badge">
                {variant === "volontaire" ? t("imposter.roleBluff") : t("imposter.roleImposter")}
              </span>
            )}

            <div className="imp-answer">
              <input
                ref={inputRef}
                value={answer}
                disabled={waiting}
                placeholder={waiting ? t("imposter.nextQuestion") : t("quiz.yourAnswer")}
                onChange={e => onChange(e.target.value)}
                autoFocus
              />
            </div>
            {waiting && <p className="imp-waiting">{t("imposter.waitingOthers")}</p>}
          </>
        )}
      </div>

      {/* Accusations — secret until the end of the round */}
      <aside className="imp-players">
        {["manches", "taupe", "volontaire"].includes(variant) && (
          <p className="imp-players-hint">
            {myVote ? t("imposter.youAccuse", { name: pseudoOf(myVote) })
              : variant === "manches" ? t("imposter.oneVotePerRound")
              : variant === "volontaire" ? t("imposter.votesLeft", { count: accuseTokens })
              : t("imposter.oneVotePerReign")}
          </p>
        )}
        {voteError && <p className="imp-vote-error">{voteError}</p>}
        {players.map(p => {
          const accused = myVote === p.playerId;
          return (
            <div key={p.playerId}
              style={bannerStyle(p)}
              className={`imp-player${p.playerId === myId ? " me" : ""}${p.offline ? " offline" : ""}${accused ? " accused" : ""}${p.cosmetics?.banner ? " has-banner" : ""}`}>
              <PlayerAvatar player={p} size={30} />
              <PlayerName player={p} />
              {p.score !== undefined && <b className="imp-player-score">{p.score}</b>}
              {canVote && p.playerId !== myId && (
                <button className="imp-accuse-btn" title={t("imposter.accuse", { name: p.pseudo })} onClick={() => accuse(p.playerId)}>
                  🎭
                </button>
              )}
              {accused && <span className="imp-accused-mark">🎭</span>}
            </div>
          );
        })}
      </aside>
    </div>
  );
}

export default ImposterQuiz;
